/**
 * ResetPassword.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from './ToastContainer';
import './Login.css';
import './ForgotPassword.css';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [passwordReset, setPasswordReset] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    if (!tokenFromUrl) {
      setError('Invalid or missing reset token');
    } else {
      setToken(tokenFromUrl);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newPassword || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, newPassword }),
      });

      const data = await response.json();

      if (response.ok) {
        setPasswordReset(true);
        showSuccess('Password reset successfully! Redirecting to login...');
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(data.error || 'Failed to reset password');
        showError(data.error || 'Failed to reset password');
      }
    } catch (error) {
      setError('An error occurred. Please try again.');
      showError('Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  if (!token && !passwordReset) {
    return (
      <div className="login-container forgot-password-container">
        <div className="login-box forgot-password-box">
          <h2>Invalid Reset Link</h2>
          <div className="success-message-container">
            <div className="error-icon" style={{ fontSize: '4rem', margin: '20px 0', color: '#f85149' }}>❌</div>
            <p className="success-description">
              This password reset link is invalid or has expired.
            </p>
            <p className="success-instructions">
              Please request a new password reset link to continue.
            </p>
            <div className="form-footer" style={{ marginTop: '2rem' }}>
              <p><Link to="/forgot-password">Request New Reset Link</Link></p>
              <p><Link to="/login">Back to Sign In</Link></p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container forgot-password-container">
      <div className="login-box forgot-password-box">
        <h2>Reset Password</h2>
        <p className="login-subtitle">
          <Link to="/register">Sign Up</Link> · <Link to="/login">Sign In</Link>
        </p>
        
        {!passwordReset ? (
          <form onSubmit={handleSubmit}>
            <p className="form-description">
              Enter your new password below.
            </p>

            {error && <p className="error-message">{error}</p>}
            
            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  id="newPassword"
                  name="newPassword"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    if (error) setError('');
                  }}
                  required
                  autoFocus
                  placeholder="Enter new password (min 6 characters)"
                  minLength="6"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  title={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                      <line x1="1" y1="1" x2="23" y2="23"></line>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <input
                type={showPassword ? "text" : "password"}
                id="confirmPassword"
                name="confirmPassword"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (error) setError('');
                }}
                required
                placeholder="Re-enter new password"
                minLength="6"
              />
            </div>

            <button type="submit" disabled={isLoading} className="login-button">
              {isLoading ? 'Resetting Password...' : 'Reset Password'}
            </button>
          </form>
        ) : (
          <div className="success-message-container">
            <div className="success-icon">✓</div>
            <h3>Password Reset Successful!</h3>
            <p className="success-description">
              Your password has been successfully reset and your email address is now verified.
            </p>
            <p className="success-instructions">
              You can now sign in with your new password. Redirecting to sign-in page in 3 seconds...
            </p>
            <div className="form-footer" style={{ marginTop: '2rem' }}>
              <p><Link to="/login">Go to Sign In Now</Link></p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;

